import Sound from '@/webgl/audio/Sound'
import SoundObserver, { SoundTime } from '@/webgl/audio/SoundObserver'
import SubtitleTrack from '@/webgl/audio/SubtitleTrack'
import Signal from '@/webgl/core/Signal'

export type SubtitleEvent = {
  track: SubtitleTrack
  time: number
}

export default class SubtitleLibrary {
  private _record: Record<string, SubtitleTrack> = {}
  private _observer: SoundObserver
  private _activeTrack: SubtitleTrack

  onTrackChange: Signal<SubtitleEvent> = new Signal<SubtitleEvent>()
  onTrackUpdate: Signal<SubtitleEvent> = new Signal<SubtitleEvent>()

  update(name: string, sound: Sound) {
    if (!this.has(name)) return

    this._observer?.dispose()

    this._activeTrack = this.get(name)

    this._observer = new SoundObserver()
    this._observer.observe(sound)
    this._observer.onTimeUpdate.on(this.onTimeUpdate)
    this._observer.onEnded.on(this.onEnded)
    this.onTrackChange.emit({ track: this._activeTrack, time: 0 })
  }

  onTimeUpdate = (time: SoundTime) => {
    this.onTrackUpdate.emit({
      track: this._activeTrack,
      time: time.bufferTime,
    })
  }

  onEnded = () => {
    this._activeTrack = null
    this._observer = null
    this.onTrackChange.emit({ track: null, time: 0 })
  }

  has(id: string): boolean {
    return this._record[id] !== undefined
  }

  add(id: string, track: SubtitleTrack) {
    if (this.has(id)) throw new Error(`SubtitleLibrary: Subtitle track with id ${id} already exists`)
    this._record[id] = track
  }

  remove(id: string) {
    delete this._record[id]
  }

  get(id: string): SubtitleTrack {
    if (!this.has(id)) throw new Error(`SubtitleLibrary: No subtitle track with id ${id}`)
    return this._record[id]
  }
}
